import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../services/api';

// Formater un montant pour jsPDF (pas de caractères spéciaux, espaces normaux)
const fmtPrix = (n) => {
  const val = parseFloat(n || 0);
  const parts = val.toFixed(2).split('.');
  // Ajouter separateur milliers avec espace normal (pas \u00A0)
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};

// Helper pour récupérer le nom entreprise (cache en mémoire)
let _cachedNomEntreprise = null;
export const getNomEntreprise = async () => {
  if (_cachedNomEntreprise) return _cachedNomEntreprise;
  try {
    const r = await fetch(`${API_URL}/parametres/bijouterie`);
    const d = await r.json();
    _cachedNomEntreprise = d?.nom_entreprise || 'Bijouterie';
    return _cachedNomEntreprise;
  } catch { return 'Bijouterie'; }
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF FACTURE PRO UNIFIÉ (LOT 2, 11/06/2026)
// Remplace : le PDF violet inline de Factures.js + les 2 variantes vente
// (devenues des wrappers). Mention CGI francaise (29B) retiree.
// Dégradé élégant : tout champ/image manquant => bloc omis, jamais de "undefined".
// ─────────────────────────────────────────────────────────────────────────────

// Charge une image (URL serveur) en dataURL pour jsPDF. null si échec (bloc omis).
const loadImageDataURL = async (url) => {
  if (!url) return null;
  try {
    const abs = url.startsWith('http') ? url : `${API_URL.replace(/\/api$/, '')}${url.startsWith('/') ? url : '/' + url}`;
    const res = await fetch(abs);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch (e) { return null; }
};

let _cachedEntreprise = null;
export const getEntreprise = async () => {
  if (_cachedEntreprise) return _cachedEntreprise;
  try {
    const r = await fetch(`${API_URL}/parametres/bijouterie`);
    if (r.ok) { _cachedEntreprise = await r.json(); return _cachedEntreprise; }
  } catch (e) { /* degrade */ }
  return {};
};

/**
 * PDF facture professionnel unifié.
 * facture : {numero_facture, date_facture, mode_paiement, client_nom, client_telephone,
 *            client_adresse, client_ice, lignes[], montant_ht, tva, montant_ttc, taux_tva, notes}
 * options : { cadeau: bool (AUCUN prix nulle part), mention: string }
 */
export const generateFactureProPDF = async (facture, entrepriseIn = null, options = {}) => {
  const cadeau = !!options.cadeau;
  const e = entrepriseIn || await getEntreprise();
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const noir = [25, 25, 30], or = [185, 155, 60], gris = [120, 120, 120],
        blanc = [255, 255, 255], fond = [252, 250, 245];
  const nettoie = (v) => (v === null || v === undefined) ? '' : String(v).trim();

  const [logo, cachet, signature] = await Promise.all([
    loadImageDataURL(e.logo_url), loadImageDataURL(e.cachet_url), loadImageDataURL(e.signature_url),
  ]);

  // fond + bandeau
  doc.setFillColor(...fond); doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...noir); doc.rect(0, 0, W, 42, 'F');
  doc.setFillColor(...or);   doc.rect(0, 42, W, 1.5, 'F');

  let xTexte = 20;
  if (logo) {
    try { doc.addImage(logo, 'PNG', 14, 6, 30, 30, undefined, 'FAST'); xTexte = 50; } catch (err) { /* omis */ }
  }
  doc.setTextColor(...blanc); doc.setFontSize(19); doc.setFont('helvetica', 'bold');
  doc.text(nettoie(e.nom_entreprise) || 'BIJOUTERIE', xTexte, 17);
  doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(...or);
  if (nettoie(e.slogan)) doc.text(nettoie(e.slogan), xTexte, 24);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(220, 220, 220); doc.setFontSize(8);
  const coords = [
    nettoie(e.adresse),
    [nettoie(e.code_postal), nettoie(e.ville)].filter(Boolean).join(' '),
    nettoie(e.telephone) ? `Tél : ${nettoie(e.telephone)}` : '',
    nettoie(e.email),
  ].filter(Boolean);
  coords.slice(0, 2).forEach((l, i) => doc.text(String(l), xTexte, 30 + i * 4.5));
  if (coords.length > 2) doc.text(coords.slice(2).join('  ·  '), W - 20, 36, { align: 'right' });

  doc.setTextColor(...blanc); doc.setFontSize(22); doc.setFont('helvetica', 'bold');
  doc.text(cadeau ? 'FACTURE CADEAU' : 'FACTURE', W - 20, 18, { align: 'right' });

  // infos + client
  let y = 54;
  doc.setTextColor(60, 60, 60); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  if (nettoie(facture.numero_facture)) doc.text(`N° : ${nettoie(facture.numero_facture)}`, 20, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  const dt = facture.date_facture ? new Date(facture.date_facture) : null;
  if (dt && !isNaN(dt)) doc.text(`Date : ${dt.toLocaleDateString('fr-FR')}`, 20, y + 6);
  if (!cadeau && nettoie(facture.mode_paiement)) doc.text(`Règlement : ${nettoie(facture.mode_paiement)}`, 20, y + 12);

  doc.setFont('helvetica', 'bold'); doc.setTextColor(...or);
  doc.text('CLIENT', W - 90, y);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
  let yc = y + 6;
  [
    nettoie(facture.client_nom),
    nettoie(facture.client_telephone) ? `Tél : ${nettoie(facture.client_telephone)}` : '',
    nettoie(facture.client_adresse),
    nettoie(facture.client_ice) ? `ICE : ${nettoie(facture.client_ice)}` : '',
  ].filter(Boolean).forEach(l => { doc.text(String(l).slice(0, 45), W - 90, yc); yc += 5.5; });

  y = Math.max(y + 20, yc) + 4;
  doc.setDrawColor(...or); doc.setLineWidth(0.3); doc.line(20, y, W - 20, y);

  // lignes
  const lignes = facture.lignes || [];
  const head = cadeau ? [['Désignation', 'Référence', 'Qté']]
                      : [['Désignation', 'Référence', 'Qté', 'PU (DH)', 'Montant (DH)']];
  const body = lignes.map(l => {
    const base = [nettoie(l.description) || '—', nettoie(l.reference) || '—', String(l.quantite || 1)];
    if (cadeau) return base;
    const montant = (l.montant !== undefined && l.montant !== null)
      ? l.montant : (l.quantite || 1) * (l.prix_unitaire || 0);
    return [...base, fmtPrix(l.prix_unitaire), fmtPrix(montant)];
  });
  autoTable(doc, {
    startY: y + 4,
    head,
    body: body.length ? body : [cadeau ? ['—', '—', ''] : ['—', '—', '', '', '']],
    theme: 'plain',
    headStyles: { fillColor: noir, textColor: blanc, fontStyle: 'bold', fontSize: 8.5 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40], fillColor: fond },
    alternateRowStyles: { fillColor: [247, 244, 237] },
    columnStyles: cadeau
      ? { 0: { cellWidth: 110 }, 2: { halign: 'center' } }
      : { 0: { cellWidth: 72 }, 2: { halign: 'center', cellWidth: 14 }, 3: { halign: 'right', cellWidth: 30 }, 4: { halign: 'right', cellWidth: 32 } },
    margin: { left: 20, right: 20 },
  });
  let yT = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y + 20) + 10;

  // totaux : 3 lignes HT / TVA / TTC toujours (sauf cadeau : aucun prix)
  if (!cadeau) {
    const tx = W - 95;
    const tva = parseFloat(facture.tva || 0);
    const tauxTxt = tva > 0
      ? ` (${facture.taux_tva ?? (facture.montant_ht > 0 ? (tva / facture.montant_ht * 100).toFixed(2) : '')}%)`
      : '';
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    doc.text('Montant HT :', tx, yT);
    doc.text(`${fmtPrix(facture.montant_ht)} DH`, W - 20, yT, { align: 'right' });
    doc.text(`TVA${tauxTxt} :`, tx, yT + 6);
    doc.text(`${fmtPrix(tva)} DH`, W - 20, yT + 6, { align: 'right' });
    doc.setDrawColor(...or); doc.setLineWidth(0.4); doc.line(tx, yT + 9.5, W - 20, yT + 9.5);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...noir);
    doc.text('Total TTC :', tx, yT + 16);
    doc.text(`${fmtPrix(facture.montant_ttc)} DH`, W - 20, yT + 16, { align: 'right' });
    yT += 24;
  } else {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(...gris);
    doc.text('Document offert — sans valeur de prix.', 20, yT);
    yT += 8;
  }

  if (nettoie(facture.notes)) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...gris);
    doc.text(`Note : ${nettoie(facture.notes)}`.slice(0, 110), 20, yT + 2);
    yT += 8;
  }

  // cachet + signature
  const yVisuels = Math.min(Math.max(yT + 6, H - 70), H - 64);
  if (signature) { try { doc.addImage(signature, 'PNG', 24, yVisuels, 42, 24, undefined, 'FAST'); } catch (err) { /* omis */ } }
  if (cachet)    { try { doc.addImage(cachet, 'PNG', W - 66, yVisuels, 42, 24, undefined, 'FAST'); } catch (err) { /* omis */ } }

  // pied de page
  doc.setDrawColor(...or); doc.setLineWidth(0.5); doc.line(20, H - 26, W - 20, H - 26);
  doc.setTextColor(...or); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Merci pour votre confiance', W / 2, H - 20, { align: 'center' });
  doc.setTextColor(...gris); doc.setFontSize(7);
  const legal = [
    nettoie(e.numero_ice) ? `ICE : ${nettoie(e.numero_ice)}` : '',
    nettoie(e.numero_registre_commerce) ? `RC : ${nettoie(e.numero_registre_commerce)}` : '',
    nettoie(e.numero_if) ? `IF : ${nettoie(e.numero_if)}` : '',
    nettoie(e.numero_patente) ? `Patente : ${nettoie(e.numero_patente)}` : '',
  ].filter(Boolean);
  if (legal.length) doc.text(legal.join('   ·   '), W / 2, H - 15, { align: 'center' });
  const mention = nettoie(options.mention) || nettoie(e.mentions_legales);
  if (mention) doc.text(mention.slice(0, 120), W / 2, H - 10.5, { align: 'center' });

  const nomFichier = `${cadeau ? 'FACTURE_CADEAU' : 'FACTURE'}_${(nettoie(facture.numero_facture).replace(/[^a-zA-Z0-9-]/g, '_')) || 'document'}.pdf`;
  doc.save(nomFichier);
};

// Adapte une VENTE (point de vente) au format facture normalisé.
const venteVersFacture = (vente, bijou = {}) => ({
  numero_facture: vente.numero_facture || `V-${String(vente.id || '').padStart(6, '0')}`,
  date_facture: vente.date_vente || new Date().toISOString(),
  mode_paiement: vente.mode_paiement,
  client_nom: vente.client_nom,
  client_telephone: vente.client_telephone,
  lignes: [{
    description: bijou.nom || vente.bijou_nom || vente.designation || 'Bijou',
    reference: bijou.id_produit || bijou.reference_article || vente.reference || '',
    quantite: vente.quantite || 1,
    prix_unitaire: vente.prix_vente,
    montant: vente.prix_vente,
  }],
  // vente de bijou = PRODUIT : sans TVA (ht = ttc)
  montant_ht: vente.prix_vente, tva: 0, montant_ttc: vente.prix_vente,
  notes: vente.commentaire,
});

// Wrappers de compatibilité — les anciens templates sont remplacés par le PDF pro.
export const generateFacturePDF = async (vente) => {
  const mention = vente.mode_paiement === 'especes_caisse2'
    ? 'Ce document ne constitue pas un justificatif fiscal.' : '';
  return generateFactureProPDF(venteVersFacture(vente), null, { mention });
};

export const generateFacturePremiumPDF = (vente, bijou = {}) =>
  generateFactureProPDF(venteVersFacture(vente, bijou), null, {});

export const generateFicheFabricationPDF = (ordre, bijou = {}, etapes = [], composants = [], pierres = []) => {
  const doc = new jsPDF();
  const gold = [180, 140, 50];
  const dark = [30, 30, 30];

  // En-tête
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('FICHE DE FABRICATION', 105, 18, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`${ordre.numero_ordre || 'OF-??????'}`, 105, 28, { align: 'center' });
  doc.setFontSize(9);
  doc.text(`Imprime le ${new Date().toLocaleDateString('fr-FR')}`, 105, 35, { align: 'center' });

  // Infos bijou + photo
  let y = 50;
  doc.setTextColor(0, 0, 0);
  const imageUrl = bijou.image_url || ordre.image_url;
  const hasImage = !!imageUrl;

  // Bloc infos (gauche si photo, sinon pleine largeur)
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, hasImage ? 125 : 180, 40, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text(`Ref : ${bijou.reference_article || ordre.ref_article || '—'}`, 20, y + 8);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`ID : ${bijou.id_produit || '—'}`, 20, y + 15);
  doc.text(`Type : ${ordre.type_bijou || bijou.categorie || '—'}`, 20, y + 22);
  doc.text(`Or : ${ordre.couleur_or || bijou.couleur_or || '—'}`, 20, y + 29);
  doc.text(`Collection : ${bijou.collection_nom || '—'}`, 20, y + 36);
  doc.text(`Client : ${ordre.client_nom || bijou.client_nom || 'Bijouterie'}`, 85, y + 15);
  if (bijou.description && bijou.description !== bijou.reference_article) {
    doc.text(`${bijou.description.substring(0, 35)}`, 85, y + 22);
  }

  // Photo à droite (si disponible)
  if (hasImage) {
    try {
      doc.addImage(imageUrl, 'JPEG', 142, y, 50, 40, undefined, 'FAST');
    } catch (e) {
      // Si l'image ne peut pas être ajoutée (CORS etc.) — cadre vide
      doc.setFillColor(240, 240, 240);
      doc.rect(142, y, 50, 40, 'F');
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text('Photo', 167, y + 22, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
  }

  y += 48;

  // Composants fabrication
  if (composants.length > 0) {
    doc.setFillColor(...gold);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('COMPOSANTS DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Composant', 'Quantite', 'Prix unit.', 'Total']],
      body: composants.map(c => [
        c.nom || '—',
        String(c.quantite || 1),
        `${parseFloat(c.prix_unitaire || c.cout || 0).toFixed(2)} DH`,
        `${parseFloat(c.cout_total || 0).toFixed(2)} DH`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...dark], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Pierres
  if (pierres.length > 0) {
    doc.setFillColor(80, 80, 180);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PIERRES', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Designation', 'Qte', 'Carats']],
      body: pierres.map(p => [
        p.type_pierre || '—',
        p.designation || p.reference_pierre || '—',
        String(p.quantite || 1),
        `${parseFloat(p.poids_carats || 0).toFixed(2)} ct`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [80, 80, 180], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Étapes de fabrication
  if (etapes.length > 0) {
    doc.setFillColor(40, 120, 60);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ETAPES DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Etape', 'Ouvrier', 'Temps estime', 'Temps reel', 'Statut']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || e.mission || '—',
        e.ouvrier_nom || '—',
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
        e.duree_reelle_heures ? `${parseFloat(e.duree_reelle_heures).toFixed(1)}h` : '—',
        e.statut === 'terminee' ? 'FAIT' : e.statut === 'en_cours' ? 'EN COURS' : 'A FAIRE',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [40, 120, 60], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Zone poids — à remplir à la main
  if (y < 220) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.rect(15, y + 5, 180, 40);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PESEE (a remplir par l\'ouvrier)', 20, y + 13);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text('Or donne : .............. g', 20, y + 22);
    doc.text('Poids bijou fini : .............. g', 20, y + 30);
    doc.text('Or rendu : .............. g', 120, y + 22);
    doc.text('Temps total : .............. min', 120, y + 30);
    doc.text('Signature ouvrier : ..............................', 20, y + 38);
  }

  // Pied de page
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`Fiche fabrication ${ordre.numero_ordre || ''} - ${bijou.reference_article || ''}`, 105, 291, { align: 'center' });

  doc.save(`FICHE_FAB_${ordre.numero_ordre || 'OF'}_${bijou.reference_article || ''}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// FICHE PIÈCE INDIVIDUELLE — Une fiche par pièce dans un OF multi-bijoux
// ═══════════════════════════════════════════════════════════════════════════
export const generateFichePiecePDF = (ordre, piece, composants = [], pierres = [], etapes = []) => {
  const doc = new jsPDF();
  const gold = [180, 140, 50];
  const dark = [30, 30, 30];

  // Poids unitaire : priorité poids_unitaire de l'OF, sinon poids_metal / quantite_pieces
  const qte = parseInt(ordre.quantite_pieces || 1);
  const poidsTotal = parseFloat(ordre.poids_metal || ordre.poids_total_or || 0);
  const poidsUnitaire = parseFloat(ordre.poids_unitaire || 0) || (qte > 0 ? poidsTotal / qte : 0);

  // Couleur de la pièce (peut différer de l'OF si modifiée)
  const couleurPiece = piece.couleur_or || ordre.couleur_or || '—';
  const ouvrierPiece = piece.ouvrier_nom || '—';
  const refPiece = piece.reference_piece || `${ordre.numero_ordre}-${piece.numero_piece}`;

  // ── En-tête ──
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('FICHE DE FABRICATION', 105, 16, { align: 'center' });
  doc.setFontSize(13);
  doc.text(refPiece, 105, 27, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(`Pièce ${piece.numero_piece} / ${qte}   —   Imprimé le ${new Date().toLocaleDateString('fr-FR')}`, 105, 35, { align: 'center' });

  // ── Note importante ──
  let y = 50;
  if (ordre.notes) {
    doc.setFillColor(255, 235, 235);
    doc.rect(15, y, 180, 12, 'F');
    doc.setTextColor(180, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(`/!\\ NOTE : ${ordre.notes.substring(0, 90)}`, 20, y + 8);
    doc.setTextColor(0, 0, 0);
    y += 16;
  }

  // ── Bloc infos bijou ──
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 180, 46, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`Réf : ${ordre.ref_article || '—'}`, 20, y + 8);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);

  // Colonne gauche
  doc.text(`Type : ${ordre.type_bijou || '—'}`, 20, y + 16);
  doc.text(`Or : ${couleurPiece}`, 20, y + 23);
  doc.text(`Genre : ${ordre.genre || '—'}`, 20, y + 30);
  doc.text(`Collection : ${ordre.collection_nom || ordre.client_nom || 'Bijouterie'}`, 20, y + 37);

  // Colonne droite — poids UNITAIRE (pas total OF)
  doc.setFont(undefined, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(160, 100, 0);
  doc.text(`${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) : '—'} g`, 165, y + 18, { align: 'right' });
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Poids OR brut', 165, y + 25, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(`Ouvrier : ${ouvrierPiece}`, 115, y + 35);

  if (ordre.description) {
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text(`${ordre.description.substring(0, 50)}`, 115, y + 43);
    doc.setTextColor(0, 0, 0);
  }

  // Options finition
  const opts = [];
  if (ordre.rhodiage) opts.push('Rhodiage');
  if (piece.couleur_or === 'BLANC' || piece.couleur_or === 'ROSE') opts.push('→ Rhodiage selon couleur');
  if (ordre.ecrin) opts.push('Écrin');
  if (ordre.gravure_beldi) opts.push('Gravure beldi');
  if (ordre.garantie) opts.push('Garantie');
  if (ordre.fonte2) opts.push('2ème fonte');
  if (opts.length > 0) {
    doc.setFontSize(8);
    doc.setTextColor(50, 100, 50);
    doc.text(`Options : ${opts.join(' | ')}`, 20, y + 44);
    doc.setTextColor(0, 0, 0);
  }

  y += 52;

  // ── Composants fabrication ──
  if (composants.length > 0) {
    doc.setFillColor(...gold);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('COMPOSANTS DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Composant', 'Qté', 'Prix unit.', 'Total']],
      body: composants.map(c => [
        c.nom || '—',
        String(c.quantite || 1),
        `${parseFloat(c.prix_unitaire || c.cout || 0).toFixed(2)} DH`,
        `${parseFloat(c.cout_total || 0).toFixed(2)} DH`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...dark], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Pierres (divisées par quantite_pieces pour la pièce individuelle) ──
  if (pierres.length > 0) {
    doc.setFillColor(80, 80, 180);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PIERRES', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Désignation', 'Qté/pièce', 'Carats/pièce']],
      body: pierres.map(p => {
        const qteP = Math.ceil((p.quantite || 1) / qte);
        const ctsP = qte > 0 ? parseFloat(p.poids_carats || 0) / qte : parseFloat(p.poids_carats || 0);
        return [
          p.type_pierre || '—',
          p.designation || p.reference_pierre || '—',
          String(qteP),
          `${ctsP.toFixed(3)} ct`,
        ];
      }),
      theme: 'grid',
      headStyles: { fillColor: [80, 80, 180], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Étapes de fabrication (de cette pièce, ou de l'OF si pas de pièce spécifique) ──
  if (etapes.length > 0) {
    doc.setFillColor(40, 120, 60);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ÉTAPES DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Étape', 'Ouvrier', 'Tps estimé', 'Statut']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || '—',
        e.ouvrier_nom || ouvrierPiece,
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
        e.statut === 'terminee' ? '✓ FAIT' : e.statut === 'en_cours' ? '⟳ EN COURS' : '○ À FAIRE',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [40, 120, 60], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Zone pesée à remplir par l'ouvrier ──
  const yZone = Math.max(y + 3, 220);
  if (yZone < 260) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.rect(15, yZone, 180, 44, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(15, yZone, 180, 44, 'F');
    doc.rect(15, yZone, 180, 44);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PESÉE (à remplir par l\'ouvrier)', 20, yZone + 9);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`Or donné : ${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) + ' g' : '.............. g'}`, 20, yZone + 19);
    doc.text('Poids bijou fini : .............. g', 20, yZone + 27);
    doc.text('Or rendu : .............. g', 115, yZone + 19);
    doc.text('Temps total : .............. min', 115, yZone + 27);
    doc.text('Signature ouvrier : ..............................', 20, yZone + 38);
    doc.text(`Date : ....../....../......`, 130, yZone + 38);
  }

  // ── Pied de page ──
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`${refPiece} — ${ordre.type_bijou || ''} ${couleurPiece} — Pièce ${piece.numero_piece}/${qte} — ${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) + 'g' : ''}`, 105, 291, { align: 'center' });

  doc.save(`FICHE_${refPiece.replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// FICHE GÉNÉRALE DE PRODUCTION — Résumé pour le chef d'atelier
// Montre tous les bijoux d'un même ordre avec leur référence individuelle
// ═══════════════════════════════════════════════════════════════════════════
export const generateFicheGeneralePDF = (ordre, enfants = [], etapes = []) => {
  const doc = new jsPDF();
  const gold  = [180, 140, 50];
  const dark  = [30, 30, 30];
  const green = [40, 120, 60];

  const qte          = parseInt(ordre.quantite_pieces || enfants.length || 1);
  const poidsTotal   = parseFloat(ordre.poids_metal || ordre.poids_total_or || 0);
  const poidsUnit    = parseFloat(ordre.poids_unitaire || 0) || (qte > 0 ? poidsTotal / qte : 0);
  const couleur      = (ordre.couleur_or || 'JAUNE').toUpperCase();
  const refArticle   = ordre.ref_article || ordre.numero_ordre || '—';
  const dateImprime  = new Date().toLocaleDateString('fr-FR');

  // ── En-tête ──────────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 44, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('ORDRE DE FABRICATION', 105, 15, { align: 'center' });
  doc.setFontSize(14);
  doc.text(`${ordre.numero_ordre}  —  ${ordre.type_bijou || 'BIJOU'} × ${qte}`, 105, 27, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(`Réf : ${refArticle}   |   Imprimé le ${dateImprime}`, 105, 37, { align: 'center' });

  // ── Bloc infos générales ──────────────────────────────────────────────────
  let y = 52;
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 180, 32, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');

  // Colonne gauche
  doc.setFont(undefined, 'bold'); doc.text('Type bijou :', 20, y + 8); doc.setFont(undefined, 'normal');
  doc.text(ordre.type_bijou || '—', 55, y + 8);
  doc.setFont(undefined, 'bold'); doc.text('Or / Couleur :', 20, y + 16); doc.setFont(undefined, 'normal');
  doc.text(couleur, 55, y + 16);
  doc.setFont(undefined, 'bold'); doc.text('Collection :', 20, y + 24); doc.setFont(undefined, 'normal');
  doc.text(ordre.collection_nom || ordre.client_nom || 'Bijouterie', 55, y + 24);

  // Colonne droite
  doc.setFont(undefined, 'bold'); doc.text('Quantité totale :', 115, y + 8); doc.setFont(undefined, 'normal');
  doc.text(`${qte} bijoux`, 160, y + 8);
  doc.setFont(undefined, 'bold'); doc.text('Poids OR / bijou :', 115, y + 16); doc.setFont(undefined, 'normal');
  doc.setTextColor(160, 100, 0);
  doc.text(`${poidsUnit > 0 ? poidsUnit.toFixed(3) : '—'} g`, 165, y + 16, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold'); doc.text('Poids OR total :', 115, y + 24); doc.setFont(undefined, 'normal');
  doc.text(`${poidsTotal > 0 ? poidsTotal.toFixed(3) : '—'} g`, 165, y + 24, { align: 'right' });

  y += 38;

  // ── Note si présente ──────────────────────────────────────────────────────
  if (ordre.notes) {
    doc.setFillColor(255, 235, 235);
    doc.rect(15, y, 180, 10, 'F');
    doc.setTextColor(180, 0, 0);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text(`/!\\${ordre.notes.substring(0, 100)}`, 20, y + 7);
    doc.setTextColor(0, 0, 0);
    y += 14;
  }

  // ── Tableau des bijoux (1 ligne par child OF) ─────────────────────────────
  doc.setFillColor(...gold);
  doc.rect(15, y, 180, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text(`BIJOUX DE CET ORDRE (${qte})`, 20, y + 6);
  y += 10;

  const statutLabel = (s) => {
    if (!s) return 'En attente';
    if (s === 'terminé' || s === 'termine' || s === 'terminee') return '✓ Terminé';
    if (s === 'en_cours') return '⟳ En cours';
    if (s === 'en_attente') return '○ En attente';
    return s;
  };

  autoTable(doc, {
    startY: y,
    head: [['#', 'Référence bijou', 'Couleur / Or', 'Ouvrier assigné', 'Statut', 'Avancement']],
    body: enfants.length > 0
      ? enfants.map((e, i) => {
          const nbEt  = parseInt(e.nb_etapes || 0);
          const nbDone = parseInt(e.etapes_terminees || 0);
          const pct   = nbEt > 0 ? Math.round(nbDone / nbEt * 100) : 0;
          return [
            String(e.piece_index || i + 1),
            e.numero_ordre || e.reference_piece || `—`,
            (e.couleur_or || couleur),
            e.artisan_nom || e.ouvrier_nom || '—',
            statutLabel(e.statut),
            nbEt > 0 ? `${nbDone}/${nbEt} (${pct}%)` : '—',
          ];
        })
      : Array.from({ length: qte }, (_, i) => [
          String(i + 1),
          `${ordre.numero_ordre}-${i + 1}`,
          couleur, '—', 'En attente', '—',
        ]),
    theme: 'grid',
    headStyles: { fillColor: [...dark], fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 42 }, 4: { cellWidth: 28 }, 5: { cellWidth: 32 } },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Étapes communes ───────────────────────────────────────────────────────
  if (etapes.length > 0 && y < 240) {
    doc.setFillColor(...green);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ÉTAPES DE FABRICATION (communes à tous les bijoux)', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Étape', 'Tps estimé']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || '—',
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...green], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 10 }, 2: { cellWidth: 22 } },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Zone validation chef d'atelier ────────────────────────────────────────
  if (y < 255) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.4);
    doc.rect(15, y, 180, 22);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Validation chef d\'atelier :', 20, y + 8);
    doc.setFont(undefined, 'normal');
    doc.text('Signature : ......................................', 20, y + 17);
    doc.text('Date : ....../....../......', 130, y + 17);
  }

  // ── Pied de page ──────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(
    `${ordre.numero_ordre} — ${ordre.type_bijou || ''} ${couleur} — ${qte} bijoux — Or total : ${poidsTotal > 0 ? poidsTotal.toFixed(3) + 'g' : '—'}`,
    105, 291, { align: 'center' }
  );

  doc.save(`OF_GENERAL_${(ordre.numero_ordre || 'OF').replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// ÉTIQUETTE BIJOU — Petite étiquette à coller/attacher au bijou
// ═══════════════════════════════════════════════════════════════════════════
export const generateEtiquetteBijouPDF = (bijou, options = {}) => {
  // Format étiquette : 80mm x 40mm (landscape)
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [40, 80] });

  const ref = bijou.reference_article || bijou.reference || '—';
  const idp = bijou.id_produit || '';
  const prix = bijou.pvp_magasin || bijou.prix_vente || 0;
  const couleur = bijou.couleur_or || '';
  const poids = bijou.poids_metal || bijou.poids_reel || bijou.poids_final_sans_pierres || '';
  const collection = bijou.collection_nom || '';

  // Cadre
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(1, 1, 78, 38);

  // Référence (gros)
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(ref, 40, 8, { align: 'center' });

  // ID produit
  doc.setFontSize(7);
  doc.setFont(undefined, 'normal');
  doc.text(idp, 40, 12, { align: 'center' });

  // Ligne séparatrice
  doc.setLineWidth(0.2);
  doc.line(5, 14, 75, 14);

  // Détails
  doc.setFontSize(8);
  if (couleur) doc.text(`Or ${couleur.toLowerCase()} 18k`, 5, 19);
  if (poids) doc.text(`${parseFloat(poids).toFixed(2)}g`, 55, 19);
  if (collection) doc.text(collection, 5, 24);

  // Prix (gros en bas)
  if (prix > 0 && !options.hidePrix) {
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`${fmtPrix(prix)} DH`, 40, 33, { align: 'center' });
  }

  // Code sous le prix
  doc.setFontSize(6);
  doc.setFont(undefined, 'normal');
  doc.text(`${ref} | ${idp}`, 40, 37, { align: 'center' });

  doc.save(`ETIQUETTE_${ref}_${idp}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// ÉTIQUETTES EN LOT — Plusieurs étiquettes sur une page A4
// ═══════════════════════════════════════════════════════════════════════════
export const generateEtiquettesLotPDF = (bijoux) => {
  const doc = new jsPDF();
  const cols = 3;
  const rows = 7;
  const w = 60;
  const h = 38;
  const mx = 10;
  const my = 8;
  let idx = 0;

  bijoux.forEach((bijou, i) => {
    if (i > 0 && i % (cols * rows) === 0) doc.addPage();
    const pos = i % (cols * rows);
    const col = pos % cols;
    const row = Math.floor(pos / cols);
    const x = mx + col * (w + 3);
    const y = my + row * (h + 2);

    const ref = bijou.reference_article || bijou.reference || '—';
    const idp = bijou.id_produit || '';
    const prix = bijou.pvp_magasin || bijou.prix_vente || 0;
    const couleur = bijou.couleur_or || '';
    const poids = bijou.poids_metal || bijou.poids_reel || '';

    doc.setDrawColor(180);
    doc.setLineWidth(0.2);
    doc.rect(x, y, w, h);

    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(ref, x + w/2, y + 7, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.text(idp, x + w/2, y + 11, { align: 'center' });

    doc.setLineWidth(0.1);
    doc.line(x + 3, y + 13, x + w - 3, y + 13);

    doc.setFontSize(7);
    if (couleur) doc.text(`Or ${couleur.toLowerCase()} 18k`, x + 3, y + 18);
    if (poids) doc.text(`${parseFloat(poids).toFixed(2)}g`, x + w - 3, y + 18, { align: 'right' });

    if (prix > 0) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`${fmtPrix(prix)} DH`, x + w/2, y + 30, { align: 'center' });
    }

    doc.setFontSize(5);
    doc.setFont(undefined, 'normal');
    doc.text(`${ref} | ${idp}`, x + w/2, y + 36, { align: 'center' });
  });

  doc.save(`ETIQUETTES_LOT_${new Date().toISOString().split('T')[0]}.pdf`);
};


export const generateDevisPDF = async (devis, couts = {}) => {
  const doc = new jsPDF();
  const nom = await getNomEntreprise();
  const gold = [212, 175, 55];
  const dark = [44, 62, 80];

  // En-tête
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 48, 'F');
  doc.setFillColor(...gold);
  doc.rect(0, 48, 210, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont(undefined, 'bold');
  doc.text(nom.toUpperCase(), 105, 18, { align: 'center' });
  doc.setFontSize(14);
  doc.text('DEVIS', 105, 30, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const ref = devis.reference_devis || devis.reference_article || `DEV-${String(devis.id).padStart(5, '0')}`;
  doc.text(`Ref: ${ref}`, 105, 40, { align: 'center' });

  let y = 60;
  doc.setTextColor(0, 0, 0);

  // Infos devis
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 85, 35, 'F');
  doc.rect(110, y, 85, 35, 'F');

  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('DEVIS', 20, y + 10);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Date: ${new Date(devis.date_creation || Date.now()).toLocaleDateString('fr-FR')}`, 20, y + 18);
  doc.text(`Type: ${devis.type_bijou || ''}`, 20, y + 24);
  doc.text(`Or: ${devis.couleur_or || ''}`, 20, y + 30);

  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('CLIENT', 115, y + 10);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(devis.client_nom || 'Non specifie', 115, y + 18);
  if (devis.date_livraison_souhaitee) {
    doc.text(`Livraison souhaitee: ${new Date(devis.date_livraison_souhaitee).toLocaleDateString('fr-FR')}`, 115, y + 24);
  }
  if (devis.type_creation) {
    doc.text(`Commande: ${devis.type_creation}`, 115, y + 30);
  }

  y += 45;

  // Description
  if (devis.description) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Description:', 15, y);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(devis.description, 180);
    doc.text(lines, 15, y + 6);
    y += 6 + lines.length * 5;
  }

  y += 5;

  // Tableau des coûts
  const lignes = [];
  const coutMetal = parseFloat(couts.cout_metal || devis.cout_metal || 0);
  const coutPierres = parseFloat(couts.cout_pierres || devis.cout_pierres || 0);
  const coutMO = parseFloat(couts.cout_main_oeuvre || devis.cout_main_oeuvre || 0);
  const coutFab = parseFloat(couts.cout_fabrication || devis.cout_fabrication || 0);
  const coutPack = parseFloat(couts.cout_packaging || devis.cout_packaging || 0);

  if (coutMetal > 0) lignes.push(['Metal precieux (Or ' + (devis.couleur_or || '18k') + ')', coutMetal.toFixed(2) + ' MAD']);
  if (coutPierres > 0) lignes.push(['Pierres precieuses', coutPierres.toFixed(2) + ' MAD']);
  if (coutMO > 0) lignes.push(["Main d'oeuvre", coutMO.toFixed(2) + ' MAD']);
  if (coutFab > 0) lignes.push(['Couts de fabrication', coutFab.toFixed(2) + ' MAD']);
  if (coutPack > 0) lignes.push(['Packaging / Ecrin', coutPack.toFixed(2) + ' MAD']);

  if (lignes.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Poste', 'Montant']],
      body: lignes,
      theme: 'grid',
      headStyles: { fillColor: dark, textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Totaux
  const prixRevient = coutMetal + coutPierres + coutMO + coutFab + coutPack;
  const coeff = parseFloat(devis.coefficient_marge || 1.5);
  const prixVente = parseFloat(devis.prix_vente_final || devis.prix_vente || (prixRevient * coeff));

  doc.setFillColor(245, 245, 245);
  doc.rect(110, y, 85, 30, 'F');
  doc.setFontSize(10);
  doc.text('Prix de revient:', 115, y + 8);
  doc.text(`${prixRevient.toFixed(2)} MAD`, 190, y + 8, { align: 'right' });
  doc.text(`Coefficient: x${coeff}`, 115, y + 16);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...gold);
  doc.text('Prix de vente:', 115, y + 26);
  doc.text(`${prixVente.toFixed(2)} MAD`, 190, y + 26, { align: 'right' });

  y += 40;
  doc.setTextColor(0, 0, 0);

  // Conditions
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Ce devis est valable 30 jours a compter de sa date de creation.', 15, y);
  doc.text("Un acompte de 50% est demande a la commande, le solde a la livraison.", 15, y + 5);
  doc.text("Les delais de fabrication sont donnes a titre indicatif et peuvent varier.", 15, y + 10);

  // Pied de page
  doc.setFillColor(...dark);
  doc.rect(0, 282, 210, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`${nom} - Document genere le ${new Date().toLocaleDateString('fr-FR')}`, 105, 290, { align: 'center' });

  doc.save(`DEVIS_${ref}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export
export default {
  generateFacturePDF,
  generateFacturePremiumPDF,
  generateFicheFabricationPDF,
  generateEtiquetteBijouPDF,
  generateEtiquettesLotPDF,
  generateDevisPDF,
};
